import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

/** Fires once when the element scrolls into view. */
export function useInView<T extends HTMLElement>(
  rootMargin = "0px 0px -12% 0px",
) {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin, threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, seen]);

  return { ref, seen };
}

type RevealProps = {
  children: ReactNode;
  /** Stagger in ms. */
  delay?: number;
  className?: string;
  as?: "div" | "span" | "li" | "section";
};

/** Content rises sharply from below and fades in, once, on first view. */
export function Reveal({
  children,
  delay = 0,
  className = "",
  as = "div",
}: RevealProps) {
  const { ref, seen } = useInView<HTMLDivElement>();
  const Tag = as;
  return (
    <div className={`reveal-clip ${className}`}>
      <Tag
        ref={ref as never}
        className={`reveal ${seen ? "is-in" : ""}`}
        style={{ transitionDelay: `${delay}ms` }}
      >
        {children}
      </Tag>
    </div>
  );
}

/**
 * Mount-time cascade: children stagger via --i when the parent gets `.is-in`.
 * Use for a whole card that should appear as one beat after data loads.
 */
export function Cascade({
  children,
  className = "",
  resetKey,
}: {
  children: ReactNode;
  className?: string;
  /** Remount / replay when this changes (e.g. handle). */
  resetKey?: string;
}) {
  const [on, setOn] = useState(false);

  useEffect(() => {
    setOn(false);
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setOn(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [resetKey]);

  return (
    <div className={`cascade reveal-clip ${on ? "is-in" : ""} ${className}`}>
      {children}
    </div>
  );
}

export function CascadeItem({
  i,
  children,
  className = "",
  as: Tag = "div",
}: {
  i: number;
  children: ReactNode;
  className?: string;
  as?: "div" | "span" | "li" | "section";
}) {
  return (
    <Tag
      className={`cascade-item ${className}`}
      style={{ ["--i" as string]: i } as CSSProperties}
    >
      {children}
    </Tag>
  );
}

function easeOutExpo(t: number) {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

/** Counts from 0 to `value` when scrolled into view (or when `active`). */
export function CountUp({
  value,
  duration = 1100,
  decimals = 0,
  className = "",
  active,
}: {
  value: number;
  duration?: number;
  decimals?: number;
  className?: string;
  /** Force start (e.g. cascade already on). Default: in-view. */
  active?: boolean;
}) {
  const { ref, seen } = useInView<HTMLSpanElement>();
  const go = active ?? seen;
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!go) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setN(value);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      setN(value * easeOutExpo(p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [go, value, duration]);

  return (
    <span ref={ref} className={className}>
      {n.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
    </span>
  );
}
